import React, { useState, useEffect } from 'react';
import { UserPlus, X, Key, Fingerprint, AlertTriangle, Users } from 'lucide-react';
import { API, authFetch } from '../lib/api';
import SimpleERPGrid from '../components/erp/SimpleERPGrid';

export default function AccessControl() {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newUser, setNewUser]   = useState({ name: '', role: 'Staff Viewer' });
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    authFetch(`${API}/users`).then(r => r.json()).then(d => { setUsers(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const provision = async () => {
    setSaving(true);
    try {
      const res  = await authFetch(`${API}/users`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newUser) });
      const data = await res.json();
      setUsers(p => [...p, data]);
      setShowModal(false);
      setNewUser({ name: '', role: 'Staff Viewer' });
    } catch { alert('Failed to provision entity.'); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (user) => {
    const updated = { ...user, status: user.status === 'Active' ? 'Locked' : 'Active' };
    await authFetch(`${API}/users/${user.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: updated.status }) });
    setUsers(p => p.map(u => u.id === user.id ? updated : u));
  };

  const roles = [
    { id: 'admin',   name: 'System Admin',  modules: ['Payroll', 'Inventory', 'Sales', 'HR', 'Settings'], color: 'badge-red' },
    { id: 'manager', name: 'ERP Manager',   modules: ['Inventory', 'Sales', 'HR'],                        color: 'badge-blue' },
    { id: 'viewer',  name: 'Audit Viewer',  modules: ['Audit Logs'],                                      color: 'badge-slate' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ fontSize: 20, fontWeight: 900, color: '#0f172a' }}>MSN-CLIQ Identity Access Control</h2>
        <button className="btn btn-dark" onClick={() => setShowModal(true)} data-testid="provision-btn">
          <UserPlus size={13} /> Provision New Entity
        </button>
      </div>

      {/* Role cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        {roles.map(r => (
          <div className="card" key={r.id} style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 30, height: 30, background: '#f8fafc', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Users size={14} color="#64748b" />
              </div>
              <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{r.name}</span>
            </div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8, borderBottom: '1px solid #f1f5f9', paddingBottom: 6 }}>Privileges</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {r.modules.slice(0, 3).map(m => <span key={m} className={`badge ${r.color}`}>{m}</span>)}
              {r.modules.length > 3 && <span style={{ fontSize: 8, color: '#94a3b8', fontWeight: 700 }}>+{r.modules.length - 3}</span>}
            </div>
          </div>
        ))}
        <div style={{ background: '#3b82f6', borderRadius: 12, padding: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(59,130,246,0.3)' }}>
          <Fingerprint size={22} color="rgba(255,255,255,0.5)" style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>DNA Sync</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#fff' }}>99.8%</div>
        </div>
      </div>

      {/* Users grid (ERPAdvancedGrid) */}
      <SimpleERPGrid
        rows={users}
        loading={loading}
        onRefresh={() => authFetch(`${API}/users`).then(r => r.json()).then(d => setUsers(d))}
        title={`Provisioned System Entities (${users.length})`}
        testIdBase="access-control-users"
        idField="id"
        nameField="name"
        copyKeys={[
          { field: 'id',   label: 'Copy UserId' },
          { field: 'name', label: 'Copy Name' },
        ]}
        columns={[
          { field: 'name',   header: 'Entity Name', visible: true, frozen: true },
          { field: 'role',   header: 'Role',        visible: true },
          { field: 'status', header: 'Status',      visible: true },
          { field: 'dna',    header: 'DNA Anchor',  visible: true },
          { field: 'id',     header: 'UID',         visible: true },
        ]}
      />

      <div className="alert-banner amber">
        <AlertTriangle size={14} color="#d97706" style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>Global Security Access Policy</div>
          <div style={{ fontSize: 11, color: '#78350f', lineHeight: 1.6 }}>
            System access is strictly bound to the hardware signature of the originating node.
            IP-locking is active for all SQL connections. Attempting to bypass DNA anchoring will result in immediate blockchain quarantine.
          </div>
        </div>
      </div>

      {/* Provision Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div className="card" style={{ width: 440, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontWeight: 800, fontSize: 16 }}>Provision New Entity</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} color="#94a3b8" /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label className="form-label">Full Name</label>
                <input className="form-input" value={newUser.name} onChange={e => setNewUser(p => ({...p, name: e.target.value}))} placeholder="Entity Name" />
              </div>
              <div>
                <label className="form-label">Role</label>
                <select className="form-select" value={newUser.role} onChange={e => setNewUser(p => ({...p, role: e.target.value}))}>
                  <option>Root Owner</option><option>System Architect</option><option>Security Lead</option>
                  <option>ERP Manager</option><option>Staff Viewer</option><option>Inventory Manager</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={provision} disabled={saving || !newUser.name}>
                  {saving ? 'Provisioning...' : <><Key size={12} /> Provision Entity</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
